import EnvService from "../../../core/services/env";
import {matchPath} from "react-router-dom";

/**
 * This interface houses all the available endpoints of this application specific for the 'biz-print' server.
 */
class ApiEndpoints {

    /**
     * The base url of the api endpoints of this application.
     * @private
     */
    public static baseUrl: string = EnvService.apiEndpointUrl + '/api';

    /**
     * The controllers of the api endpoints.
     * @private
     */
    public static controllers = {
        bizFile: this.baseUrl.concat("/BizFile"),
        config: this.baseUrl.concat("/Config"),
        operator: this.baseUrl.concat("/Operator"),
        reportingTools: this.baseUrl.concat("/ReportingTools"),
        code: this.baseUrl.concat("/Code"),
        dashboard: this.baseUrl.concat("/Dashboard"),
    }

    /**
     * The endpoints specific for the biz file controller of the server api.
     */
    public static bizFile = {
        baseUrl: this.controllers.bizFile,
        uploadRemainingChunk: this.controllers.bizFile.concat("/UploadRemainingChunk"),
        createAccessToken: this.controllers.bizFile.concat("/CreateAccessToken"),
        uploadFile: this.controllers.bizFile.concat("/UploadFile"),
    }

    /**
     * The endpoints specific for the config controller of the server api.
     */
    public static config = {
        baseUrl: this.controllers.config,
        getConfig: this.controllers.config,
    }

    /**
     * The endpoints specific for the operator controller of the server api.
     */
    public static operator = {
        baseUrl: this.controllers.operator,
        getCurrentOperatorInformation: this.controllers.operator,
        getPartnerInformation: this.controllers.operator.concat("/GetPartnerInformation"),
    }


    /**
     * The endpoints specific for the reporting tools controller of the server api.
     */
    public static reportingTools = {
        baseUrl: this.controllers.reportingTools,
        getColumns: this.controllers.reportingTools.concat(`/GetColumns`),
    }


    /**
     * The endpoints specific for the code controller of the server api.
     */
    public static code = {
        baseUrl: this.controllers.code,
        get: (id: string) => this.controllers.code.concat(`/${id}`),
        create: this.controllers.code,
        update: this.controllers.code,
        delete: (id: string) => this.controllers.code.concat(`/${id}`),
        scan: (id: string) => this.controllers.code.concat(`/scan/${id}`),
        list: this.controllers.code.concat('/list'),
        getReport: this.controllers.code.concat(`/GetReport`),
    }

    /**
     * The endpoints specific for the dashboard controller of the server api.
     */
    public static dashboard = {
        baseUrl: this.controllers.dashboard,
        getDashboard: this.controllers.dashboard,
    }

    /**
     * The intent descriptions for each API endpoint.
     * @private
     */
    private static apiIntents: { [key: string]: string | { [method: string]: string } } = {
        [this.bizFile.uploadRemainingChunk]: "Upload a remaining chunk of a file.",
        [this.bizFile.uploadFile]: "Upload a file.",
        [this.bizFile.createAccessToken]: "Create an access token for a file.",
        [this.config.getConfig]: "Fetch system settings.",
        [this.reportingTools.getColumns]: "Retrieve reporting tools data including columns.",
        [this.operator.getCurrentOperatorInformation]: "Retrieve current operator information.",
        [this.operator.getPartnerInformation]: "Retrieve partner information.",
    };

    /**
     * Get the intent description for a given API endpoint and method.
     *
     * @param endpoint      the API endpoint.
     * @param method        the HTTP method.
     * @returns             the intent description.
     */
    public static getIntent(endpoint: string, method?: string): string | undefined {
        const normalize = (str: string) => str
            .replace(this.baseUrl, '')
            .replace(/\?.*$/, '')
        const normalizedEndpoint = normalize(endpoint);
        const endpoints = Object.keys(this.apiIntents);
        const matchedEndpoints = endpoints
            .map(path => matchPath({path: normalize(path), end: false, caseSensitive: false}, normalizedEndpoint))
            .map((e, index) => {
                if (!e) return e;
                return endpoints[index];
            })
            .filter(match => !!match)
        if (matchedEndpoints.length <= 0) {
            return undefined;
        }

        let matchedEndpoint;
        if (matchedEndpoints.length > 1) {
            matchedEndpoint = matchedEndpoints.find(e => e === endpoint) ?? matchedEndpoints[0];
        } else {
            matchedEndpoint = matchedEndpoints[0]
        }

        const intent = this.apiIntents[matchedEndpoint!];
        if (typeof intent === 'string') {
            return intent;
        }
        return intent?.[method ?? ""];
    }
}

export default ApiEndpoints;
